'use client';

import { useEffect, useState, useRef } from 'react';
import { useRouter, usePathname, useParams } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { useUser } from '../context/user';
import { UserSummary } from '@/types/user';
import { Client, ClientList } from '@/types/client';
import { useMutation } from '@tanstack/react-query';
import { getClient as getClientApi, getClients } from '@/api/client';
import { getUserWithTimezone as getUserApi } from '@/api/user';
import { resolveTargetClientId, isAuthorizedClientId, isClientIdFormat } from '@/lib/client-id';
import { bootstrapAuthenticatedUser } from '@/lib/auth-bootstrap';
import {
  canFetchClientDetails,
  requiresInitialSetup,
  shouldLeaveSetupRoute,
} from '@/app/lib/onboarding-routes';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

import StatusCard from './ui/StatusCard';
import Spinner from './ui/Spinner';
import { identifyMatticUser } from '@/lib/clarity';

function buildClientScopedPath(path: string, canonicalId: string, urlClientId?: string) {
  if (urlClientId && isClientIdFormat(urlClientId)) {
    return path.replace(`/${urlClientId}`, `/${canonicalId}`);
  }

  if (path === '/' || path === '/signin' || path === '/start') {
    return `/${canonicalId}/home`;
  }

  return `/${canonicalId}${path.startsWith('/') ? path : `/${path}`}`;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [fatalError, setFatalError] = useState<Error | null>(null);
  const { currentUser, loading } = useAuth();
  const router = useRouter();
  const { clientId: urlClientId } = useParams();
  const pathname = usePathname();
  const {
    user,
    userSummary,
    client,
    clientSummary,
    clientList,
    setUser,
    setUserSummary,
    setClient,
    setClientSummary,
    setClientList,
  } = useUser();
  const navigationRef = useRef(false);
  const isInternalDashboardRoute = pathname?.startsWith('/dashboard') || false;
  const isOpsDashboardRoute = pathname?.startsWith('/ops-dashboard') || false;
  const isHubspotCallback = pathname?.includes('/hubspot-callback') || false;
  const isSalesforceCallback = pathname?.includes('/salesforce-callback') || false;
  const isSlackCallback = pathname?.includes('/slack-callback') || false;
  const isLinkedinAdsCallback = pathname?.includes('/linkedin-ads-callback') || false;
  const isAttioCallback = pathname?.includes('/attio-callback') || false;
  const isPipedriveCallback = pathname?.includes('/pipedrive-callback') || false;
  const isMailboxCallback = pathname?.includes('/mailbox-connect/callback') || false;
  const isSetupRoute = pathname === '/setup';
  const shouldSkipClientCheck =
    isInternalDashboardRoute ||
    isOpsDashboardRoute ||
    isHubspotCallback ||
    isSalesforceCallback ||
    isSlackCallback ||
    isLinkedinAdsCallback ||
    isAttioCallback ||
    isPipedriveCallback ||
    isMailboxCallback ||
    isSetupRoute;
  const effectiveUser = user || userSummary;
  const normalizedUrlClientId = Array.isArray(urlClientId) ? urlClientId[0] : urlClientId;
  const currentFullClientId = normalizedUrlClientId || '';
  const resolvedClientId =
    client?.id ||
    clientSummary?.id ||
    (effectiveUser
      ? resolveTargetClientId(effectiveUser.id, effectiveUser.client_ids || [])
      : null);
  const canLoadFullClient = canFetchClientDetails(clientSummary);
  const resolvedPostSetupPath = resolvedClientId ? `/${resolvedClientId}/home` : '/';
  const shouldRedirectAwayFromSetup =
    isSetupRoute && shouldLeaveSetupRoute(effectiveUser, clientSummary);

  const prependClientToList = (list: ClientList, clientToAdd: Client): ClientList => ({
    ...list,
    data: [clientToAdd, ...list.data.filter(client => client.id !== clientToAdd.id)],
    total: list.data.some(client => client.id === clientToAdd.id) ? list.total : list.total + 1,
  });

  const getFallbackUrlClientId = (
    currentUser: Pick<UserSummary, 'type' | 'client_ids'>,
    listedIds: string[]
  ): string | null => {
    if (!normalizedUrlClientId) return null;

    if (listedIds.includes(normalizedUrlClientId)) return null;

    if (currentUser.type === 'internal') {
      return normalizedUrlClientId;
    }

    if (currentUser.type === 'agency' && currentUser.client_ids?.includes(normalizedUrlClientId)) {
      return normalizedUrlClientId;
    }

    return null;
  };

  const { mutate: loginMutation } = useMutation({
    mutationFn: (idToken: string) =>
      bootstrapAuthenticatedUser({
        idToken,
        preferredClientId: normalizedUrlClientId,
      }),
    onSuccess: data => {
      if (data) {
        setUser(null);
        setUserSummary(data.userSummary);
        setClientSummary(data.clientSummary);
        // Track user in Clarity - do not remove this
        if (process.env.NEXT_PUBLIC_APP_ENV === 'production') {
          // Inject Clarity script dynamically if not already loaded
          if (typeof window !== 'undefined' && !window.clarity) {
            const script = document.createElement('script');
            script.id = 'clarity-script';
            script.async = true;
            script.textContent = `
              (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
              })(window, document, "clarity", "script", "tlwz2zeh3b");
            `;
            document.head.appendChild(script);
          }
          identifyMatticUser(
            data.userSummary.email, // user email
            data.activeClientId ||
              currentFullClientId ||
              data.userSummary.client_ids?.[0] ||
              'unknown', // active client ID
            data.userSummary.id, // user ID
            data.userSummary.type, // user type (internal/external)
            data.clientSummary?.name // company name
          );
        }
      }
    },
  });
  const { mutateAsync: fetchUserById } = useMutation({
    mutationFn: ({ clientId, userId }: { clientId: string; userId: string }) =>
      getUserApi(clientId, userId),
    onSuccess: data => {
      setUser(data);
    },
  });
  const { mutateAsync: fetchClientById } = useMutation({
    mutationFn: ({ id }: { id: string }) => getClientApi(id),
    onSuccess: data => {
      setClient(data);
      if (effectiveUser?.type === 'external') {
        setClientList({
          data: [data],
          ending_before: '',
          has_more: false,
          starting_after: '',
          total: 1,
        });
      }
      if (effectiveUser && !user) {
        fetchUserById({ clientId: data.id, userId: effectiveUser.id });
        // setIsLoading(false) is deferred — useEffect handles it after user is set
      } else {
        setIsLoading(false);
      }
    },
    onError: (error: Error) => {
      setIsLoading(false);

      if (effectiveUser && effectiveUser.client_ids && effectiveUser.client_ids.length > 0) {
        const resolveId = resolveTargetClientId(
          effectiveUser.id,
          effectiveUser.client_ids,
          normalizedUrlClientId
        );
        const safeClientId = resolveId || effectiveUser.client_ids[0];
        const currentId = Array.isArray(urlClientId) ? urlClientId[0] : urlClientId;
        if (currentId !== safeClientId) {
          router.replace(`/${safeClientId}/home`);
          return;
        }
      }
      setFatalError(error);
    },
  });

  const { mutateAsync: fetchClients } = useMutation({
    mutationFn: () => getClients(),
    onSuccess: async clientData => {
      if (clientData.data.length > 0 && effectiveUser) {
        const listedIds = clientData.data.map(c => c.id);
        const fallbackUrlClientId = getFallbackUrlClientId(effectiveUser, listedIds);
        let nextClientList = clientData;

        if (fallbackUrlClientId) {
          try {
            const fetchedUrlClient = await getClientApi(fallbackUrlClientId);
            nextClientList = prependClientToList(clientData, fetchedUrlClient);
          } catch (error) {
            console.error('Failed to fetch client from URL, falling back to first client', error);
          }
        }

        const authorizedIds = nextClientList.data.map(c => c.id);
        const resolveId = resolveTargetClientId(
          effectiveUser.id,
          authorizedIds,
          normalizedUrlClientId
        );

        setClientList(nextClientList);

        if (resolveId) {
          const canonicalId = resolveId;
          fetchClientById({ id: resolveId }).then(() => {
            const path = window.location.pathname;
            if (effectiveUser.type === 'internal') {
              if (path === '/' || path === '/signin' || path === '/start') {
                router.replace('/ops-dashboard');
              } else if (
                normalizedUrlClientId !== canonicalId &&
                !path.startsWith('/ops-dashboard') &&
                !path.startsWith('/dashboard') &&
                !path.startsWith('/agency') &&
                !shouldSkipClientCheck
              ) {
                const newPath = buildClientScopedPath(path, canonicalId, normalizedUrlClientId);
                router.replace(newPath);
              }
            } else if (effectiveUser.type === 'agency') {
              if (path === '/' || path === '/signin' || path === '/start') {
                router.replace('/dashboard');
              } else if (
                normalizedUrlClientId !== canonicalId &&
                !path.startsWith('/dashboard') &&
                !path.startsWith('/agency') &&
                !shouldSkipClientCheck
              ) {
                const newPath = buildClientScopedPath(path, canonicalId, normalizedUrlClientId);
                router.replace(newPath);
              }
            } else if (normalizedUrlClientId !== canonicalId && !shouldSkipClientCheck) {
              const newPath = buildClientScopedPath(path, canonicalId, normalizedUrlClientId);
              router.replace(newPath);
            }
          });
        }
      } else {
        setClient(null);
        setClientList(null);
        setIsLoading(false);
      }
    },
    onError: (error: Error) => {
      setIsLoading(false);
      setFatalError(error);
    },
  });

  const getClient = async (activeUser: Pick<UserSummary, 'id' | 'type' | 'client_ids'>) => {
    if (!currentUser) return;
    setIsLoading(true);

    if (
      activeUser?.type === 'external' &&
      activeUser.client_ids &&
      activeUser.client_ids.length > 0
    ) {
      if (!canLoadFullClient) {
        setIsLoading(false);
        return;
      }

      const resolveId = resolveTargetClientId(
        activeUser.id,
        activeUser.client_ids,
        normalizedUrlClientId
      );

      if (resolveId) {
        await fetchClientById({ id: resolveId });
        const path = window.location.pathname;

        if (normalizedUrlClientId !== resolveId && !shouldSkipClientCheck) {
          const newPath = buildClientScopedPath(path, resolveId, normalizedUrlClientId);
          router.replace(newPath);
        }
      } else {
        setIsLoading(false);
      }
    } else if (!clientList || clientList.data.length === 0) {
      await fetchClients();
    } else if (!client || !hasFetchedClient.current) {
      let nextClientList = clientList;
      const fallbackUrlClientId = getFallbackUrlClientId(
        activeUser,
        clientList.data.map(c => c.id)
      );

      if (fallbackUrlClientId) {
        try {
          const fetchedUrlClient = await getClientApi(fallbackUrlClientId);
          nextClientList = prependClientToList(clientList, fetchedUrlClient);
          setClientList(nextClientList);
        } catch (error) {
          console.error('Failed to fetch client from URL, falling back to first client', error);
        }
      }

      const authorizedIds = nextClientList.data.map(c => c.id);
      const resolveId = resolveTargetClientId(activeUser.id, authorizedIds, normalizedUrlClientId);

      const path = window.location.pathname;
      if (
        (activeUser.type === 'internal' || activeUser.type === 'agency') &&
        (path.startsWith('/dashboard') ||
          path.startsWith('/ops-dashboard') ||
          path.startsWith('/agency'))
      ) {
        setIsLoading(false);
        return;
      }

      if (resolveId) {
        await fetchClientById({ id: resolveId });
      } else {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  const getUser = async () => {
    if (!currentUser) return;
    const idToken = await currentUser.getIdToken();
    loginMutation(idToken);
    // setIsLoading(false);
  };

  const hasFetchedClient = useRef(false);
  useEffect(() => {
    if (navigationRef.current || fatalError) return;

    if (!loading && !currentUser) {
      navigationRef.current = true;
      router.replace('/signin');
    } else if (currentUser && !userSummary) {
      getUser();
    } else if (shouldRedirectAwayFromSetup) {
      router.replace(resolvedPostSetupPath || '/');
    } else if (requiresInitialSetup(effectiveUser, clientSummary) && pathname !== '/setup') {
      router.replace('/setup');
    } else if (
      effectiveUser &&
      !user &&
      ((clientSummary?.id && canLoadFullClient) ||
        (effectiveUser.type === 'internal' && !!normalizedUrlClientId))
    ) {
      const clientId = (clientSummary?.id || normalizedUrlClientId)!;
      fetchUserById({ clientId, userId: effectiveUser.id });
    } else if (
      (isInternalDashboardRoute || isOpsDashboardRoute || pathname.startsWith('/agency')) &&
      effectiveUser &&
      (effectiveUser.type === 'internal' || effectiveUser.type === 'agency')
    ) {
      if (!clientList || clientList.data.length === 0) {
        fetchClients();
      } else if (!user) {
        const dashboardClientId = resolveTargetClientId(
          effectiveUser.id,
          clientList.data.map(client => client.id),
          normalizedUrlClientId
        );

        if (dashboardClientId) {
          fetchUserById({ clientId: dashboardClientId, userId: effectiveUser.id });
        } else {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    } else if (
      effectiveUser &&
      !hasFetchedClient.current &&
      (!client ||
        (normalizedUrlClientId &&
          isAuthorizedClientId(normalizedUrlClientId, effectiveUser, clientList) &&
          client.id !== currentFullClientId))
    ) {
      if (!shouldSkipClientCheck) {
        hasFetchedClient.current = true;
        getClient(effectiveUser);
      } else {
        setIsLoading(false);
      }
    } else if (
      effectiveUser &&
      client &&
      isAuthorizedClientId(normalizedUrlClientId, effectiveUser, clientList) &&
      client.id === currentFullClientId
    ) {
      setIsLoading(false);
      hasFetchedClient.current = false;
    } else if (effectiveUser && !client && hasFetchedClient.current && isLoading) {
      setIsLoading(false);
    }
  }, [
    currentUser,
    loading,
    user,
    userSummary,
    client,
    clientSummary,
    isLoading,
    currentFullClientId,
    normalizedUrlClientId,
    clientList,
    isSetupRoute,
    resolvedPostSetupPath,
    shouldRedirectAwayFromSetup,
    effectiveUser,
    canLoadFullClient,
  ]);

  if (fatalError) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 max-w-md text-center p-6">
          <StatusCard
            type="error"
            heading="Error"
            text={fatalError.message || 'Invalid client ID.'}
          />
        </div>
      </div>
    );
  }

  if (
    loading ||
    !currentUser ||
    isLoading ||
    shouldRedirectAwayFromSetup ||
    (!shouldSkipClientCheck && !pathname.startsWith('/agency') && !client)
  ) {
    return (
      <div className="h-screen flex items-center justify-center relative">
        <Spinner />
      </div>
    );
  }

  return <>{children}</>;
}
